/**
 * A match on screen: the renderer, scene, assets, physics world, input, the match driver (the
 * local practice runner or the online session; fixed 30 Hz, interpolated), placeholder effects,
 * camera feel, and HUD publishing. Created once per mount by useGame; every await checks the
 * abort signal so a StrictMode double mount (or navigating away mid-load) tears down cleanly.
 *
 * Nothing here decides gameplay: the client renders the state and sends inputs (CLAUDE.md #1).
 */
import { featuresOf, hashArena } from '@steelwake/shared/arena';
import { ArenaWorld, initPhysics } from '@steelwake/shared/physics';
import {
  aimOrigin,
  concealmentOf,
  poseCapsule,
  rayCapsule,
  type BotLevel,
  type MatchEvent,
  type MatchState,
  type Slot,
} from '@steelwake/shared/combat';
import {
  AUDIO_BANK,
  BALANCE,
  getMech,
  getWeapon,
  type MechDefinition,
} from '@steelwake/shared/data';
import type { Loadout } from '@steelwake/shared/loadout';
import { aimDirection } from '@steelwake/shared/sim';
import { Scene, Vector3 } from 'three/webgpu';

import { hudEvents, hudFrame, resetHud, useHudStore } from '../hud/store.js';
import { buildHudSnapshot, hudEventsOf } from '../hud/snapshot.js';
import type { NetConditions } from '../net/conditioner.js';
import type { NetMetrics } from '../net/metrics.js';
import type { EffectConfig } from '../settings/presets.js';
import type { Settings } from '../settings/schema.js';
import { fetchArenaColliders, loadArena, type LoadedArena } from './assets/arena.js';
import { setAnisotropy } from './assets/dispose.js';
import { loadEnvironment } from './assets/environment.js';
import { acquireLoaders, type Loaders } from './assets/loaders.js';
import { assetUrl } from './assets/paths.js';
import type { BackendName } from './backend.js';
import { fighterSample } from './match/autopilot.js';
import {
  asMechPose,
  type MatchDescriptor,
  type MatchDriver,
  type MatchSource,
} from './match/driver.js';
import { LoadProgress } from './assets/progress.js';
import {
  killCamOver,
  PoseHistory,
  replayTime,
  startKillCam,
  type KillCam,
  type PoseSample,
} from './killcam.js';
import type { MatchParams } from './match/setup.js';
import { RenderHost, type FrameTiming, type RenderSnapshot } from './render/host.js';
import { ShadowRig } from './render/shadows.js';
import { ChaseCamera } from './sim/camera.js';
import { InputController, type InputSample } from './sim/input.js';
import { selectLocomotion } from './sim/locomotion.js';
import type { UrlOptions } from './urlParams.js';
import {
  addShake,
  cameraOffsets,
  initialCameraFx,
  kickRecoil,
  stepCameraFx,
  type CameraFxState,
} from './view/cameraFx.js';
import { AudioDirector } from '../audio/director.js';
import { matchCueIds } from '../audio/dispatch.js';
import type { AudioStats } from '../audio/engine.js';
import { audio } from '../audio/instance.js';
import { ArenaFeatureViews } from './view/arenaFeatures.js';
import { BeamRibbons } from './view/vfx/beams.js';
import { WorldDecals } from './view/vfx/decals.js';
import {
  dustKick,
  explosion,
  hazardSteam,
  impact,
  jetExhaust,
  muzzleFlash,
  overheatBurst,
  projectileTrail,
  shieldShards,
  ventSteam,
  VfxRng,
} from './view/vfx/particles/emitters.js';
import { ParticleSystem, type ParticleStats } from './view/vfx/particles/system.js';
import { DeployableViews } from './view/deployables.js';
import { LightPool } from './view/lights.js';
import { MechAssetCache } from './view/mechAsset.js';
import { lookFor, type MechLook } from './view/look.js';
import { MechView, type MeleeStyle } from './view/mechView.js';
import {
  type InstancedPool,
  ProjectilePool,
  TracerPool,
  type PoolStats,
} from './view/vfx/pools.js';

/** The Quaternius rigs face +Z; the simulation's forward is -Z at yaw 0. */
const MODEL_YAW_OFFSET = Math.PI;
/** Frames rendered before the session reports ready; the first two compile the cloak pipeline. */
const READY_FRAMES = 4;
const HUD_INTERVAL = 1 / 20;
/** Radius of the camera's collision probe, metres (covers the near plane's corners). */
const CAMERA_PROBE_RADIUS = 0.35;

export type GamePhase =
  | 'probing'
  | 'initialising'
  | 'connecting'
  | 'loading'
  /** Everything is loaded; the match starts on Deploy (Phase 8 loading screen). */
  | 'staged'
  | 'ready'
  | 'error';

export interface GameStatus {
  phase: GamePhase;
  message?: string;
  /** Byte progress of the loads, 0..1 (loading and staged). */
  progress?: number;
}

export interface GameHandlers {
  onStatus(status: GameStatus): void;
  onEscapeKey(): void;
  onPointerLockLost(): void;
  onToggleStats(): void;
  /** The setup changed in place (a rematch with a new seed): the URL should follow. */
  onSetupChange(params: MatchParams): void;
}

export interface GameOptions {
  canvas: HTMLCanvasElement;
  /** The element whose CSS size the drawing buffer follows. */
  container: HTMLElement;
  signal: AbortSignal;
  settings: Settings;
  url: UrlOptions;
  /** Where the match comes from (practice parameters, or an online room in Phase 4). */
  match: MatchSource;
  handlers: GameHandlers;
}

export type GameSnapshot = RenderSnapshot;

export interface MechSnapshot {
  position: [number, number, number];
  velocity: [number, number, number];
  yaw: number;
  grounded: boolean;
  boosting: boolean;
  energy: number;
  alive: boolean;
  tick: number;
}

export interface VfxSnapshot {
  pools: Record<string, PoolStats>;
  deployables: number;
  /** GPU particles (Phase 8): the path in use, per-pool capacities and live counts. */
  particles: ParticleStats | null;
  /** Beam ribbons lit right now. */
  beams: number;
  /** Impact marks on the arena and scorch marks held by the two mechs. */
  decals: { world: number; scorches: number };
}

interface Pools {
  tracers: TracerPool;
  projectiles: ProjectilePool;
}

function poolList(p: Pools): InstancedPool[] {
  return [p.tracers, p.projectiles];
}

/** Continuous emitters run at a rate; the accumulator carries fractions between frames. */
function pulses(acc: { value: number }, rate: number, dt: number): number {
  acc.value += rate * dt;
  const n = Math.floor(acc.value);
  acc.value -= n;
  return n;
}

/** A `#rrggbb` accent as 0..1 components (particle colours). */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
}

function meleeStyle(weaponId: string): MeleeStyle {
  if (weaponId === 'mono-katana' || weaponId === 'plasma-blades') return 'slash';
  if (weaponId === 'melee-bash') return 'kick';
  return 'punch';
}

const _v = new Vector3();
const _feet = new Vector3();

/** A copy of a drawn pose for the kill cam's history (the driver reuses its pose objects). */
function samplePose(p: {
  x: number;
  y: number;
  z: number;
  yaw: number;
  aimYaw: number;
  aimPitch: number;
}): PoseSample {
  return { x: p.x, y: p.y, z: p.z, yaw: p.yaw, aimYaw: p.aimYaw, aimPitch: p.aimPitch };
}
const _muzzle = new Vector3();
const _forward = new Vector3();
const _up = new Vector3();

type Vec3Like = { x: number; y: number; z: number };

/** The unit direction from a shot's origin to its end. */
function aimOf(origin: Vec3Like, end: Vec3Like): Vec3Like {
  const len = Math.hypot(end.x - origin.x, end.y - origin.y, end.z - origin.z) || 1;
  return { x: (end.x - origin.x) / len, y: (end.y - origin.y) / len, z: (end.z - origin.z) / len };
}

/** Damage that leaves a mark on the mech (burns, drains and the arena's own doing do not). */
const SCORCHING_KINDS = new Set<string>([
  'hitscan',
  'projectile',
  'beam',
  'melee',
  'splash',
  'ability',
  'turret',
  'field',
  'execute',
]);
/**
 * World marks are rate-limited: each one clips a decal against the arena on the CPU, so a
 * volley or a missile swarm must not build a dozen in one frame.
 */
const WORLD_DECAL_GAP = 0.08;
const BEAM_DECAL_GAP = 0.25;

export class Game {
  ready = false;
  error: string | null = null;

  private settings: Settings;
  private readonly host: RenderHost;
  /** Mech definitions by slot, known once the match source has resolved. */
  private defs: [MechDefinition, MechDefinition] | null = null;
  private mySlot: Slot = 0;
  /** Each seat's loadout as the match source named it (the welcome online). */
  private loadouts: [Loadout | null, Loadout | null] = [null, null];

  private balanceVersionInUse = 0;
  private readonly scene = new Scene();
  private readonly camera: ChaseCamera;

  private loaders: Loaders | null = null;
  private disposeEnvironment: (() => void) | null = null;
  private arena: LoadedArena | null = null;
  private world: ArenaWorld | null = null;
  private assets: MechAssetCache | null = null;
  private views: [MechView, MechView] | null = null;
  private driver: MatchDriver | null = null;
  private pools: Pools | null = null;
  private deployables: DeployableViews | null = null;
  private features: ArenaFeatureViews | null = null;
  private particles: ParticleSystem | null = null;
  private beams: BeamRibbons | null = null;
  private decals: WorldDecals | null = null;
  private director: AudioDirector | null = null;
  private descriptor: MatchDescriptor | null = null;
  private readonly history = new PoseHistory(4);
  private killcam: KillCam | null = null;
  private deployResolve: (() => void) | null = null;
  private lastNow = 0;
  private lastWorldDecal = -1;
  private lastBeamDecal = -1;
  private readonly vfxRng = new VfxRng(Date.now() & 0x7fffffff || 1);
  private readonly jetAcc = [{ value: 0 }, { value: 0 }];
  private readonly ventAcc = [{ value: 0 }, { value: 0 }];
  private readonly steamAcc = { value: 0 };
  private readonly trailAcc = { value: 0 };
  private readonly wasGrounded = [true, true];
  private lights: LightPool | null = null;
  private input: InputController | null = null;
  private shadowRig: ShadowRig | null = null;

  private fx: CameraFxState = initialCameraFx();
  private lastFovScale = 1;
  private hudTimer = 0;
  private recentEvents: MatchEvent[] = [];
  private disposed = false;
  private stopped = false;

  /**
   * Keeps the chase camera out of arena walls: a sphere the size of the near plane cast from the
   * pivot (Phase 8; a ray let the frustum's corners clip through edges). ADR-0002 query-only use.
   */
  private readonly cameraClip = (origin: Vector3, direction: Vector3, max: number) =>
    this.world?.castSphere(origin, direction, CAMERA_PROBE_RADIUS, max) ?? null;

  constructor(private readonly opts: GameOptions) {
    this.settings = opts.settings;
    this.camera = new ChaseCamera(opts.settings.fov, 16 / 9);
    this.host = new RenderHost({
      canvas: opts.canvas,
      container: opts.container,
      signal: opts.signal,
      settings: opts.settings,
      url: opts.url,
      scene: this.scene,
      camera: this.camera.camera,
      // Depth of field belongs to the hangar; the match ignores the setting (DECISIONS.md).
      mask: (config) => ({ ...config, dof: null }),
      onResize: (width, height) => {
        this.camera.setAspect(width / height);
        this.shadowRig?.onCameraChange();
      },
      onFrame: (count) => {
        if (!this.ready && count >= READY_FRAMES) {
          this.ready = true;
          this.setStatus({ phase: 'ready' });
        }
      },
    });
  }

  get backend(): BackendName | null {
    return this.host.backend;
  }

  get gpuTiming(): boolean {
    return this.host.gpuTiming;
  }

  /** Frames rendered so far (the debug API and the bench read it). */
  get frameCount(): number {
    return this.host.frameCount;
  }

  async start(): Promise<void> {
    const { signal, canvas, url, handlers } = this.opts;
    this.setStatus({ phase: 'probing' });
    const handle = await this.host.init((backend) =>
      this.setStatus({ phase: 'initialising', message: backend }),
    );
    // Never join a match from an instance that is already being torn down.
    signal.throwIfAborted();

    // The match resolves first: online it names the mechs, the slot and the arena to load.
    const desc = await this.opts.match.prepare(signal, {
      onStatus: (message) => this.setStatus({ phase: 'connecting', message }),
      url,
    });
    signal.throwIfAborted();
    this.descriptor = desc;
    const progress = new LoadProgress((fraction) =>
      this.setStatus({ phase: 'loading', progress: fraction }),
    );
    this.setStatus({ phase: 'loading', progress: 0 });
    const defs: [MechDefinition, MechDefinition] = [getMech(desc.mechs[0]), getMech(desc.mechs[1])];
    this.defs = defs;
    this.mySlot = desc.mySlot;
    this.loadouts = desc.loadouts;
    this.balanceVersionInUse = desc.balanceVersion;
    // Cue files decode ahead of the first gesture (an offline context); the music follows once
    // the match is up and the context exists.
    const cueIds = matchCueIds().filter(
      (id) => id !== AUDIO_BANK.music.match && id !== AUDIO_BANK.music.suddenDeath,
    );
    progress.track('audio', 1_500_000);
    const audioReady = audio.library
      .prefetch(cueIds, signal)
      .catch(() => undefined)
      .finally(() => progress.finish('audio'));
    audio.onCaption = (text) => hudEvents.push({ t: 'caption', text });
    const loaders = acquireLoaders(handle.renderer);
    this.loaders = loaders;
    const anisotropy = Math.min(this.host.effects.anisotropy, handle.maxAnisotropy);
    const assets = new MechAssetCache(loaders, anisotropy);
    this.assets = assets;
    // The sidecar names the arena's sky, so it comes first (a few kilobytes), then everything
    // else loads in parallel.
    const colliders = await fetchArenaColliders(desc.arenaId, signal);
    const [disposeEnvironment, arena, assetA, assetB] = await Promise.all([
      loadEnvironment(this.scene, assetUrl(colliders.environment.hdri), signal, {
        environmentIntensity: colliders.environment.environmentIntensity,
        backgroundIntensity: colliders.environment.backgroundIntensity,
        onProgress: progress.track('sky', 5_000_000),
      }),
      loadArena(
        loaders,
        desc.arenaId,
        signal,
        anisotropy,
        colliders,
        progress.track('arena', 22_000_000),
      ),
      assets.load(defs[0].chassis, signal, progress.track('mech-0', 8_000_000)),
      assets.load(defs[1].chassis, signal, progress.track('mech-1', 8_000_000)),
      initPhysics(),
      audioReady,
    ]);
    signal.throwIfAborted();
    this.disposeEnvironment = disposeEnvironment;
    this.arena = arena;
    // Online, the server names the arena content it steps; other content cannot play along.
    if (desc.arenaHash !== null && hashArena(arena.colliders) !== desc.arenaHash) {
      throw new Error('arena content differs from the server; reload the page to play');
    }
    this.scene.add(arena.root);
    this.features = new ArenaFeatureViews(this.scene, arena.colliders, arena.root);

    this.world = ArenaWorld.create(arena.colliders);
    const driver = this.opts.match.createDriver({
      world: this.world,
      arena: arena.colliders,
      e2e: url.e2e,
    });
    this.driver = driver;
    this.director = new AudioDirector(audio, this.mySlot);
    void Promise.all([
      audio.library.prefetch([AUDIO_BANK.music.match, AUDIO_BANK.music.suddenDeath], signal),
      audio.whenUnlocked(),
    ])
      .then(() => {
        if (!this.disposed && !this.stopped) this.director?.startMusic();
      })
      .catch(() => undefined);
    const views: [MechView, MechView] = [
      new MechView(assetA, lookFor(assetA.sidecar, desc.loadouts[0])),
      new MechView(assetB, lookFor(assetB.sidecar, desc.loadouts[1])),
    ];
    this.views = views;
    this.scene.add(views[0].root, views[1].root);
    this.placeViews();

    const pools: Pools = {
      tracers: new TracerPool(),
      projectiles: new ProjectilePool(),
    };
    for (const pool of poolList(pools)) pool.addTo(this.scene);
    this.pools = pools;
    // The compute kernel is WebGPU's; the WebGL 2 backend's transform feedback cannot bind the
    // five particle buffers at once, so that path steps the same buffers on the CPU.
    this.particles = new ParticleSystem(
      this.scene,
      url.particles === 'cpu' || this.host.backend === 'webgl2' ? 'cpu' : 'gpu',
    );
    this.deployables = new DeployableViews(this.scene, [defs[0].accent, defs[1].accent]);
    this.lights = new LightPool(this.scene, 3);
    // Every pool renders a zero-scale instance on the first frames so its pipeline compiles now.
    pools.tracers.spawn(0, [0, -50, 0], [0, -50.1, 0]);
    // Ribbons and decals draw their whole (empty) buffers every frame, so they compile now too.
    this.beams = new BeamRibbons(this.scene);
    this.decals = new WorldDecals(this.scene, arena.root);
    pools.projectiles.sync([{ id: -1, x: 0, y: -50, z: 0, radius: 0.01 }]);

    this.input = new InputController(
      canvas,
      {
        onEscapeKey: () => handlers.onEscapeKey(),
        onPointerLockLost: () => handlers.onPointerLockLost(),
        onToggleStats: () => handlers.onToggleStats(),
        onSwapShoulder: () => this.camera.swapShoulder(),
      },
      {
        initialYaw: driver.ownState.mechs[this.mySlot].body.yaw,
        allowPointerLock: !url.e2e,
        sensitivity: this.settings.mouseSensitivity,
        bindings: this.settings.bindings,
        gamepad: this.settings.gamepad,
      },
    );

    this.shadowRig = new ShadowRig(this.scene, this.camera.camera);
    this.shadowRig.apply(this.host.effects.shadows);
    resetHud();
    // The loading screen's Deploy click starts the match (and, being a gesture, unlocks audio
    // and takes the pointer lock); e2e and autopilot runs skip the gate unless stage=1 asks.
    if (!(url.e2e || url.autopilot) || url.stage) {
      this.setStatus({ phase: 'staged', progress: 1 });
      await new Promise<void>((resolve) => {
        this.deployResolve = resolve;
        signal.addEventListener('abort', () => resolve(), { once: true });
      });
      this.deployResolve = null;
      signal.throwIfAborted();
    }
    this.host.begin((timing) => this.frame(timing));
  }

  /** Starts a staged match (the loading screen's Deploy). */
  deploy(): void {
    if (!this.deployResolve) return;
    this.input?.requestPointerLock();
    this.deployResolve();
    this.deployResolve = null;
  }

  /** What the match is (mechs, seat, arena) once the source resolved; null before. */
  get matchDescriptor(): MatchDescriptor | null {
    return this.descriptor;
  }

  /** The kill cam (Phase 8): whether it is showing and from whose seat. */
  killcamSnapshot(): { active: boolean; killer: Slot | null } {
    return { active: this.killcam !== null, killer: this.killcam?.killer ?? null };
  }

  /** Marks the session failed (called by useGame when start() rejects). */
  fail(message: string): void {
    this.error = message;
    this.setStatus({ phase: 'error', message });
  }

  /** Applies changed settings live: rebuilds the post graph only when an effect changed. */
  applySettings(next: Settings): void {
    const fovChanged = next.fov !== this.settings.fov;
    this.settings = next;
    this.camera.setFov(next.fov * this.lastFovScale);
    if (fovChanged) this.shadowRig?.onCameraChange();
    this.input?.setSensitivity(next.mouseSensitivity);
    this.input?.setBindings(next.bindings);
    this.input?.setGamepad(next.gamepad);
    const change = this.host.applySettings(next);
    if (!this.host.renderer) return;
    this.shadowRig?.apply(change.config.shadows);
    if (change.anisotropyChanged) {
      const anisotropy = Math.min(change.config.anisotropy, this.host.maxAnisotropy);
      if (this.arena) setAnisotropy(this.arena.root, anisotropy);
      this.assets?.setAnisotropy(anisotropy);
    }
  }

  setInputEnabled(enabled: boolean): void {
    this.input?.setEnabled(enabled);
  }

  quality(): EffectConfig {
    return this.host.effects;
  }

  snapshot(): GameSnapshot {
    return this.host.snapshot();
  }

  resetStats(): void {
    this.host.resetStats();
  }

  /** Both seats' liveries and parts as drawn (the online e2e reads the opponent's back). */
  looks(): [MechLook, MechLook] | null {
    return this.views ? [this.views[0].look(), this.views[1].look()] : null;
  }

  /** The published balance version the rules run under; 0 = the built-in data (Phase 7). */
  balanceVersion(): number {
    return this.balanceVersionInUse;
  }

  /** The loadouts the match runs with, by seat. */
  matchLoadouts(): [Loadout | null, Loadout | null] {
    return this.loadouts;
  }

  /** The player's mech body as drawn (kept for the Phase 2 arena checks). */
  mechSnapshot(): MechSnapshot | null {
    const s = this.driver?.ownState.mechs[this.mySlot].body;
    if (!s) return null;
    return {
      position: [s.position.x, s.position.y, s.position.z],
      velocity: [s.velocity.x, s.velocity.y, s.velocity.z],
      yaw: s.yaw,
      grounded: s.grounded,
      boosting: s.boosting,
      energy: s.energy,
      alive: s.alive,
      tick: s.tick,
    };
  }

  /** The state the HUD shows: the server's truth online, the local state in practice. */
  matchState(): MatchState | null {
    return this.driver?.state ?? null;
  }

  /** The state the player's own mech is drawn from (the prediction online). */
  predictedState(): MatchState | null {
    return this.driver?.ownState ?? null;
  }

  lastEvents(): MatchEvent[] {
    return this.recentEvents;
  }

  /** Connection metrics for online matches; null in practice. */
  netMetrics(): NetMetrics | null {
    return this.driver?.net?.() ?? null;
  }

  /** Re-tunes the simulated link of an online match (development and e2e). */
  setNetConditions(conditions: NetConditions): void {
    this.driver?.setNetConditions?.(conditions);
  }

  /** One arena mark at most every `WORLD_DECAL_GAP` seconds (each clips geometry on the CPU). */
  private addWorldDecal(at: Vec3Like, normal: Vec3Like | null, size: number, now: number): void {
    if (!this.decals || now - this.lastWorldDecal < WORLD_DECAL_GAP) return;
    this.lastWorldDecal = now;
    this.decals.add(at, normal, size, now);
  }

  /** The accessibility slider: 1 keeps the designed shake, 0 removes it. */
  private shakeScale(): number {
    return this.settings.accessibility.screenShake;
  }

  audioSnapshot(): AudioStats {
    return audio.stats();
  }

  vfxSnapshot(): VfxSnapshot {
    const pools: Record<string, PoolStats> = {};
    if (this.pools) for (const pool of poolList(this.pools)) pools[pool.kind] = pool.stats();
    return {
      pools,
      deployables: this.deployables?.count() ?? 0,
      particles: this.particles?.stats() ?? null,
      beams: this.beams?.alive(this.lastNow) ?? 0,
      decals: {
        world: this.decals?.count(this.lastNow) ?? 0,
        scorches: this.views ? this.views[0].scorchCount() + this.views[1].scorchCount() : 0,
      },
    };
  }

  /** The practice parameters; null before the match resolves and for online matches. */
  get params(): MatchParams | null {
    return this.driver?.practice?.params ?? null;
  }

  // --- practice hooks (absent online; the runner refuses them outside practice mode) ---

  setBot(level: BotLevel): void {
    this.driver?.practice?.setBotLevel(level);
  }

  setRoundSeconds(seconds: number): void {
    this.driver?.practice?.setRoundSeconds(seconds);
  }

  skipCountdown(): void {
    this.driver?.practice?.skipCountdown();
  }

  ultimateReady(): void {
    if (!this.opts.url.e2e) return;
    this.driver?.practice?.ultimateReady();
  }

  /** Puts the own mech at a point (tests reach pads, pickups and platforms without walking). */
  teleport(x: number, y: number, z: number): void {
    if (!this.opts.url.e2e) return;
    this.driver?.practice?.teleport({ x, y, z });
  }

  /** Points the aim at the opponent's drawn centre (tests and the debug API). */
  aimAtOpponent(): void {
    const { driver, defs, input } = this;
    if (!driver || !defs || !input) return;
    const me = driver.mech(this.mySlot).pose;
    const enemy = driver.mech(driver.enemySlot).pose;
    const from = aimOrigin(asMechPose(me), defs[this.mySlot], BALANCE.aimHeightFraction);
    const to = {
      x: enemy.x,
      y: enemy.y + defs[driver.enemySlot].capsule.height / 2,
      z: enemy.z,
    };
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dz = to.z - from.z;
    input.setAim(Math.atan2(-dx, -dz), Math.atan2(dy, Math.hypot(dx, dz)));
  }

  /** Same mechs and difficulty, a fresh seed, no asset reload (practice only). */
  rematch(seed: number): void {
    const driver = this.driver;
    const practice = driver?.practice;
    if (!driver || !practice || !this.views) return;
    practice.rematch(seed);
    for (const view of this.views) view.revive();
    this.resetEffects();
    this.input?.setAim(driver.ownState.mechs[this.mySlot].body.yaw, 0.22);
    this.opts.handlers.onSetupChange(practice.params);
  }

  /**
   * Stops frames at once because the route is about to change. Disposal follows when the
   * component unmounts, but until React commits the new route the page must not spend its main
   * thread on frames, or (under software rendering) the transition starves for seconds.
   */
  stop(): void {
    this.stopped = true;
    this.host.stop();
    this.director?.stopAll();
    audio.stopMusic(1);
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    this.stopped = true;
    this.host.stop();
    this.director?.stopAll();
    this.director = null;
    audio.stopMusic(1);
    audio.onCaption = null;
    this.input?.dispose();
    this.shadowRig?.dispose();
    if (this.pools) for (const pool of poolList(this.pools)) pool.dispose();
    this.deployables?.dispose();
    this.features?.dispose();
    this.features = null;
    this.particles?.dispose();
    this.particles = null;
    this.beams?.dispose();
    this.beams = null;
    this.decals?.dispose();
    this.decals = null;
    this.lights?.dispose();
    if (this.views) for (const view of this.views) view.dispose();
    this.assets?.dispose();
    this.arena?.dispose();
    this.disposeEnvironment?.();
    this.driver?.dispose();
    this.world?.free();
    this.loaders?.release();
    resetHud();
    await this.host.dispose();
  }

  private setStatus(status: GameStatus): void {
    if (!this.disposed) this.opts.handlers.onStatus(status);
  }

  private resetEffects(): void {
    if (this.pools) for (const pool of poolList(this.pools)) pool.clear();
    this.fx = initialCameraFx();
    this.recentEvents = [];
    resetHud();
  }

  /** Positions both views from the driver's interpolated poses and drives their animation. */
  /** Poses the views from the driver, or from replayed poses during the kill cam. */
  private placeViews(dt = 0, replay?: readonly [PoseSample, PoseSample]): void {
    const { driver, views, defs } = this;
    if (!driver || !views || !defs) return;
    for (const slot of [0, 1] as const) {
      const view = views[slot];
      const replayed = replay?.[slot];
      if (replayed) {
        view.setPose(replayed.x, replayed.y, replayed.z, replayed.yaw + MODEL_YAW_OFFSET);
        continue;
      }
      const { pose, combatant: c } = driver.mech(slot);
      view.setPose(pose.x, pose.y, pose.z, pose.yaw + MODEL_YAW_OFFSET);
      const def = defs[slot];
      view.setLocomotion(
        selectLocomotion({
          speed: Math.hypot(c.body.velocity.x, c.body.velocity.z),
          grounded: c.body.grounded,
          boosting: c.body.boosting,
          walkSpeed: def.speed,
          boostSpeed: def.boostSpeed,
        }),
      );
      view.setHeat(c.heat / def.heatCap);
      view.setCloak(c.effects.some((e) => e.params.flags?.includes('cloaked')));
      if (dt > 0) view.update(dt);
      view.updateLod(this.camera.camera.position);
    }
  }

  /** Emitters that run while a state holds: jets, venting, hazard steam, trails, landings. */
  private emitContinuous(state: MatchState, now: number, dt: number): void {
    const { particles, driver, defs } = this;
    if (!particles || !driver || !defs) return;
    const rng = this.vfxRng;
    for (const slot of [0, 1] as const) {
      const c = state.mechs[slot];
      const def = defs[slot];
      const pose = driver.mech(slot).pose;
      const jetAcc = this.jetAcc[slot]!;
      const ventAcc = this.ventAcc[slot]!;
      if (c.alive && (c.body.boosting || c.body.jetting)) {
        const back = { x: Math.sin(pose.yaw), y: 0, z: Math.cos(pose.yaw) };
        const at = {
          x: pose.x + back.x * 0.6,
          y: pose.y + def.capsule.height * 0.35,
          z: pose.z + back.z * 0.6,
        };
        for (let i = pulses(jetAcc, 60, dt); i > 0; i--)
          particles.emit(jetExhaust(at, back, rng), now);
      } else {
        jetAcc.value = 0;
      }
      if (c.alive && c.venting) {
        const at = { x: pose.x, y: pose.y + def.capsule.height * 0.85, z: pose.z };
        for (let i = pulses(ventAcc, 15, dt); i > 0; i--) particles.emit(ventSteam(at, rng), now);
      } else {
        ventAcc.value = 0;
      }
      const grounded = c.body.grounded;
      if (c.alive && grounded && !this.wasGrounded[slot]) {
        particles.emit(dustKick({ x: pose.x, y: pose.y + 0.1, z: pose.z }, rng), now);
      }
      this.wasGrounded[slot] = grounded;
    }
    const hazards = this.arena?.colliders.hazards ?? [];
    if (hazards.length > 0) {
      for (let i = pulses(this.steamAcc, 10 * hazards.length, dt); i > 0; i--) {
        const h = hazards[Math.floor(rng.next() * hazards.length)];
        if (!h) continue;
        particles.emit(
          hazardSteam(
            { x: h.box.center[0], y: h.box.center[1], z: h.box.center[2] },
            { x: h.box.halfExtents[0], y: h.box.halfExtents[1], z: h.box.halfExtents[2] },
            rng,
            h.damagePerSecond > 0 ? { r: 0.9, g: 0.5, b: 0.3 } : undefined,
          ),
          now,
        );
      }
    }
    const projectiles = state.projectiles;
    if (projectiles.length > 0) {
      const n = Math.min(projectiles.length, 8);
      for (let i = pulses(this.trailAcc, 20 * n, dt); i > 0; i--) {
        const p = projectiles[Math.floor(rng.next() * n)];
        if (p) particles.emit(projectileTrail(p.position, p.velocity, rng), now);
      }
    }
  }

  private handleEvents(events: MatchEvent[], now: number): void {
    const { pools, views, lights, driver, defs, particles, beams, decals } = this;
    if (!pools || !views || !lights || !driver || !defs || !particles || !beams || !decals) return;
    const rng = this.vfxRng;
    /** The weapon's barrel tip when the model has one, else the rules' own origin. */
    const muzzleFor = (slot: Slot, aim: Vec3Like, origin: Vec3Like): [number, number, number] => {
      if (views[slot].muzzleWorld(aim, _muzzle)) return [_muzzle.x, _muzzle.y, _muzzle.z];
      return [origin.x, origin.y, origin.z];
    };
    const mySlot = this.mySlot;
    const state = driver.state;
    const me = driver.mech(mySlot).pose;
    const myDef = defs[mySlot];
    const near = (p: { x: number; y: number; z: number }): number => {
      const d = Math.hypot(p.x - me.x, p.y - me.y, p.z - me.z);
      return Math.max(0, 1 - d / 18);
    };
    for (const e of events) {
      switch (e.t) {
        case 'shot': {
          const aim = aimOf(e.origin, e.end);
          const o = muzzleFor(e.slot, aim, e.origin);
          const end = [e.end.x, e.end.y, e.end.z];
          pools.tracers.spawn(now, o, end);
          particles.emit(muzzleFlash({ x: o[0], y: o[1], z: o[2] }, aim, rng), now);
          if (e.hit !== null || e.blocked)
            particles.emit(impact(e.end, null, rng, e.hit !== null ? 6 : 10), now);
          // A pellet stopped by the arena marks it, facing back down the shot.
          if (e.hit === null && e.blocked) {
            this.addWorldDecal(e.end, { x: -aim.x, y: -aim.y, z: -aim.z }, 0.35, now);
          }
          lights.flash(o[0], o[1], o[2], 0xffc070, 30);
          views[e.slot].shoot();
          if (e.slot === mySlot) this.fx = kickRecoil(this.fx, getWeapon(e.weapon).recoil);
          break;
        }
        case 'projectileSpawn':
          particles.emit(muzzleFlash(e.position, { x: 0, y: 0.3, z: 0 }, rng), now);
          views[e.slot].shoot();
          if (e.slot === mySlot) this.fx = kickRecoil(this.fx, getWeapon(e.weapon).recoil);
          break;
        case 'projectileImpact': {
          particles.emit(impact(e.position, e.normal, rng, 14), now);
          if (e.hit === null) this.addWorldDecal(e.position, e.normal, 0.9, now);
          lights.flash(e.position.x, e.position.y, e.position.z, 0xffa050, 40);
          break;
        }
        case 'beam': {
          const aim = aimOf(e.origin, e.end);
          beams.show(
            now,
            e.slot * 8 + (e.weapon.length % 8),
            muzzleFor(e.slot, aim, e.origin),
            [e.end.x, e.end.y, e.end.z],
            getWeapon(e.weapon).beam?.width ?? 0.3,
            hexToRgb(defs[e.slot].accent),
          );
          if (e.hit !== null) particles.emit(impact(e.end, null, rng, 3), now);
          else if (now - this.lastBeamDecal >= BEAM_DECAL_GAP) {
            // A beam sweeping the arena scores it a few times a second, not every tick.
            this.lastBeamDecal = now;
            this.addWorldDecal(e.end, { x: -aim.x, y: -aim.y, z: -aim.z }, 0.5, now);
          }
          views[e.slot].shoot();
          break;
        }
        case 'explosion': {
          particles.emit(explosion(e.position, e.radius, rng), now);
          this.addWorldDecal(e.position, null, Math.max(1, e.radius * 0.8), now);
          lights.flash(e.position.x, e.position.y + 1, e.position.z, 0xff8040, 120);
          this.fx = addShake(this.fx, near(e.position) * 0.8 * this.shakeScale());
          break;
        }
        case 'hit':
          views[e.target].flinch();
          if (SCORCHING_KINDS.has(e.kind)) views[e.target].addScorch(e.position, 0.45);
          if (e.target === mySlot)
            this.fx = addShake(
              this.fx,
              Math.min(1, ((e.hp + e.shield) / myDef.hp) * 6) * this.shakeScale(),
            );
          break;
        case 'kill': {
          views[e.victim].die();
          // Destroyed: replay the last moments from the winner's seat during the pause.
          const latest = this.history.latest();
          if (e.victim === mySlot && e.killer !== null && !this.killcam && latest !== null) {
            this.killcam = startKillCam(now, e.killer, latest, state.config.roundEndSeconds);
          }
          break;
        }
        case 'melee':
          views[e.slot].playMelee(meleeStyle(e.weapon));
          particles.emit(impact(e.position, { x: 0, y: 1, z: 0 }, rng, 8), now);
          break;
        case 'shieldBroken': {
          const c = state.mechs[e.slot];
          const def = defs[e.slot];
          particles.emit(
            shieldShards(
              {
                x: c.body.position.x,
                y: c.body.position.y + def.capsule.height * 0.55,
                z: c.body.position.z,
              },
              hexToRgb(def.accent),
              rng,
            ),
            now,
          );
          break;
        }
        case 'overheat': {
          const c = state.mechs[e.slot];
          const def = defs[e.slot];
          particles.emit(
            overheatBurst(
              {
                x: c.body.position.x,
                y: c.body.position.y + def.capsule.height * 0.8,
                z: c.body.position.z,
              },
              rng,
            ),
            now,
          );
          break;
        }
        case 'roundStart':
          for (const view of views) view.revive();
          for (const pool of poolList(pools)) pool.clear();
          particles.clear();
          beams.clear();
          decals.clear();
          this.killcam = null;
          this.history.clear();
          this.fx = initialCameraFx();
          // Spawns swap between rounds: face the enemy again instead of the old direction.
          this.input?.setAim(driver.ownState.mechs[mySlot].body.yaw, 0.22);
          break;
        default:
          break;
      }
    }
    for (const event of hudEventsOf(events, mySlot, state)) hudEvents.push(event);
    this.director?.dispatch(events, state);
  }

  /** The per-frame work the host runs before rendering: simulation, views, camera, HUD. */
  private frame(timing: FrameTiming): void {
    const { driver, views, input, pools, deployables, lights, defs } = this;
    if (
      this.disposed ||
      this.stopped ||
      !driver ||
      !views ||
      !input ||
      !pools ||
      !deployables ||
      !lights ||
      !defs
    )
      return;
    const { frameMs, dt, now } = timing;
    // Any key skips the kill cam; it also ends on its own clock.
    if (this.killcam && (killCamOver(this.killcam, now) || input.anyPressed)) this.killcam = null;

    // One input sample per frame; the driver latches it into every fixed step it covers
    // (DECISIONS.md #16).
    const mySlot = this.mySlot;
    let sample: InputSample;
    if (this.opts.url.autopilot) {
      sample = fighterSample(driver.ownState, mySlot, now);
      input.setAim(sample.aimYaw, sample.aimPitch);
    } else {
      sample = input.sample(dt);
    }
    const events = driver.advance(frameMs, sample);
    if (events.length > 0) {
      this.handleEvents(events, now);
      this.recentEvents = events;
    }
    const state = driver.state;

    // --- views, projectiles, deployables, effects ---
    this.placeViews(dt);
    this.history.push({
      t: now,
      poses: [samplePose(driver.mech(0).pose), samplePose(driver.mech(1).pose)],
    });
    const replay = this.killcam ? this.history.at(replayTime(this.killcam, now)) : null;
    if (replay) this.placeViews(0, replay);
    pools.projectiles.sync(
      driver.projectiles().map((p, i) => ({
        id: i,
        x: p.x,
        y: p.y,
        z: p.z,
        radius: getWeapon(p.weapon).projectile?.radius ?? 0.3,
      })),
    );
    deployables.sync(driver.deployables(), now);
    this.features?.update(driver.features(), driver.viewTick(), now);
    this.emitContinuous(state, now, dt);
    this.director?.frame(state, dt);
    this.particles?.update(this.host.renderer, now, dt);
    this.beams?.update(now);
    this.decals?.update(now);
    this.lastNow = now;
    for (const pool of poolList(pools)) pool.update(now, dt);
    lights.update(dt);

    // --- camera: chase transform, then feel offsets (the kill cam rides the winner's seat) ---
    const killerPose = this.killcam && replay ? replay[this.killcam.killer] : null;
    if (killerPose) {
      _feet.set(killerPose.x, killerPose.y, killerPose.z);
      this.camera.update(_feet, killerPose.aimYaw, killerPose.aimPitch, dt, this.cameraClip);
    } else {
      const myPose = driver.mech(mySlot).pose;
      _feet.set(myPose.x, myPose.y, myPose.z);
      const primary = getWeapon(defs[mySlot].weapons[0]!.id);
      const zooming = primary.zoom && sample.secondary && driver.ownState.mechs[mySlot].alive;
      this.fx = stepCameraFx(this.fx, dt, zooming ? 1 : 0);
      const offsets = cameraOffsets(this.fx);
      this.camera.update(_feet, sample.aimYaw, sample.aimPitch, dt, this.cameraClip);
      this.camera.camera.rotateX(offsets.pitch);
      this.camera.camera.rotateY(offsets.yaw);
      this.camera.camera.rotateZ(offsets.roll);
      _forward.set(0, 0, -1).applyQuaternion(this.camera.camera.quaternion);
      _up.set(0, 1, 0).applyQuaternion(this.camera.camera.quaternion);
      audio.setListener({ position: this.camera.camera.position, forward: _forward, up: _up });
      audio.update();
      if (Math.abs(offsets.fovScale - this.lastFovScale) > 0.001) {
        this.lastFovScale = offsets.fovScale;
        this.camera.setFov(this.settings.fov * offsets.fovScale);
        this.shadowRig?.onCameraChange();
      }
    }

    this.publishHud(state, sample, dt);
    hudFrame.scoreboard = input.scoreboard;
    hudFrame.killcam = this.killcam
      ? (driver.online?.()?.callsigns[this.killcam.killer] ?? defs[this.killcam.killer].name)
      : null;

    // Warm-up: the first two frames render both mechs cloaked so that pipeline exists before play.
    if (this.frameCount < 2) for (const view of views) view.setCloak(true);
    else if (this.frameCount === 2) for (const view of views) view.setCloak(false);
  }

  /** The 20 Hz snapshot plus the per-frame DOM values (nameplate, crosshair, vignette). */
  private publishHud(state: MatchState, sample: InputSample, dt: number): void {
    const { driver, defs } = this;
    if (!driver || !defs) return;
    const mySlot = this.mySlot;
    const enemySlot = driver.enemySlot;
    const features = this.arena?.colliders ? featuresOf(this.arena.colliders) : featuresOf(null);
    this.hudTimer += dt;
    if (this.hudTimer >= HUD_INTERVAL) {
      this.hudTimer = 0;
      useHudStore
        .getState()
        .publish(
          buildHudSnapshot(
            state,
            mySlot,
            sample.aimYaw,
            driver.online?.() ?? null,
            this.settings.accessibility.palette,
            [
              concealmentOf(features, state.mechs[0], defs[0]) !== null,
              concealmentOf(features, state.mechs[1], defs[1]) !== null,
            ],
          ),
        );
    }
    // The nameplate and the aim check follow the enemy as drawn, not as last simulated.
    const enemyFrame = driver.mech(enemySlot);
    const enemy = enemyFrame.combatant;
    const enemyPose = enemyFrame.pose;
    const enemyDef = defs[enemySlot];
    const concealed = concealmentOf(features, enemy, enemyDef);
    const cloaked = enemy.effects.some((e) => e.params.flags?.includes('cloaked'));
    _v.set(enemyPose.x, enemyPose.y + enemyDef.capsule.height + 0.6, enemyPose.z);
    const distance = _v.distanceTo(this.camera.camera.position);
    _v.project(this.camera.camera);
    const rect = this.opts.container.getBoundingClientRect();
    const onScreen = _v.z < 1 && Math.abs(_v.x) < 1.05 && Math.abs(_v.y) < 1.05;
    hudFrame.nameplate.x = ((_v.x + 1) / 2) * rect.width;
    hudFrame.nameplate.y = ((1 - _v.y) / 2) * rect.height;
    hudFrame.nameplate.visible =
      onScreen &&
      enemy.alive &&
      (!cloaked || distance < 8) &&
      (!concealed || distance < concealed.revealDistance);
    hudFrame.nameplate.distance = distance;

    // Crosshair spread and ammo follow the own prediction; the vignette follows the truth.
    const me = driver.ownState.mechs[mySlot];
    const myDef = defs[mySlot];
    const weapon = getWeapon(me.weapons[0]?.id ?? myDef.weapons[0]!.id);
    const runtime = me.weapons[0];
    const spreadDeg = weapon.hitscan
      ? weapon.hitscan.spreadDeg + weapon.hitscan.maxBloomDeg * (runtime?.bloom ?? 0)
      : 0;
    const fovRad = (this.camera.camera.fov * Math.PI) / 180;
    hudFrame.crosshairSpread =
      (Math.tan((spreadDeg * Math.PI) / 180) / Math.tan(fovRad / 2)) * (rect.height / 2);
    const hpFraction = state.mechs[mySlot].hp / myDef.hp;
    hudFrame.lowHp = hpFraction < 0.35 ? 1 - hpFraction : 0;
    const origin = aimOrigin(
      asMechPose(driver.mech(mySlot).pose),
      myDef,
      BALANCE.aimHeightFraction,
    );
    hudFrame.aimingAtEnemy =
      enemy.alive &&
      rayCapsule(
        origin,
        aimDirection(sample.aimYaw, sample.aimPitch),
        120,
        poseCapsule(asMechPose(enemyPose), enemyDef),
      ) !== null;
  }

  /** Resolves the GPU timestamp queries of the last frame without ever awaiting in the loop. */
}
